import { type ReassignmentStatus, reassignmentByIdOptions } from '@cartwheel/api/v1'
import { getAvailableReassignmentActions, ReassignmentDetailModal } from '@cartwheel/components'
import { useQuery } from '@tanstack/react-query'
import { useReassignmentActions } from '../hooks/use-reassignment-actions'
import { ClinicianHeader } from './clinician-header'
import { ReassignmentTypeDisplay } from './reassignment-type-display'
import { ReassignmentWarnings } from './reassignment-warnings'

type ReassignmentAction = Exclude<ReassignmentStatus, 'initiated'>

interface ReassignmentDetailModalProps {
  reassignmentId: string | null
  isOpen: boolean
  onClose: () => void
  canReview: boolean
}

export function ReassignmentDetailWrapper({
  reassignmentId,
  isOpen,
  onClose,
  canReview,
}: ReassignmentDetailModalProps) {
  const {
    selectedAction,
    setSelectedAction,
    additionalDetails,
    setAdditionalDetails,
    submitError,
    handleSubmit,
    resetForm,
    isPending,
    isEditing,
    editValues,
    setEditValues,
    startEdit,
    cancelEdit,
    handleSaveEdit,
    isSaving,
    editError,
  } = useReassignmentActions(reassignmentId, onClose)

  const { data, isLoading, error } = useQuery({
    ...reassignmentByIdOptions({ path: { id: reassignmentId ?? '' } }),
    enabled: !!reassignmentId && isOpen,
  })

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const availableActions = getAvailableReassignmentActions(data ?? null)

  return (
    <ReassignmentDetailModal
      isOpen={isOpen}
      onClose={handleClose}
      isLoading={isLoading}
      error={error ? 'Error loading reassignment details' : null}
      data={data ?? null}
      canReview={canReview}
      canEdit={canReview}
      isEditing={isEditing}
      onStartEdit={() => startEdit(data)}
      onCancelEdit={cancelEdit}
      editValues={editValues}
      onEditValuesChange={setEditValues}
      onSaveEdit={handleSaveEdit}
      isSaving={isSaving}
      editError={editError}
      selectedAction={selectedAction}
      onActionChange={(action: ReassignmentAction) => setSelectedAction(action)}
      additionalDetails={additionalDetails}
      onAdditionalDetailsChange={setAdditionalDetails}
      onSubmit={handleSubmit}
      isPending={isPending}
      submitError={submitError}
      actionOptions={availableActions}
      renderClinicianHeader={(clinician) => <ClinicianHeader clinician={clinician} />}
      renderTypeDisplay={(reassignment) => <ReassignmentTypeDisplay reassignment={reassignment} />}
      renderWarnings={(reassignment, action) => (
        <ReassignmentWarnings reassignment={reassignment} selectedAction={action} />
      )}
    />
  )
}
